import type { SiteShot } from '../../shared/siteContent';

/**
 * A product screenshot in a browser-chrome frame. The images are a Site
 * content library entry (078): a capture in client/public/marketing, taken
 * from the running product (`scripts/marketing-shots.mjs` for the desktop
 * captures, `scripts/marketing-shots-mobile.mjs` for the `-m` phone crops),
 * or an upload that replaced it — so the site shows the real thing.
 *
 * `width`/`height` are the capture's own pixels, given so the browser reserves
 * the right box before the PNG arrives instead of reflowing the page around
 * it. Every desktop capture is 2880×1800, which is the default — a shot of a
 * different size passes its own.
 *
 * `mobileSrc` is a narrow crop of the same screen, served under 640px through
 * `<picture>`: a 980px-wide capture scaled into a 343px column is decoration,
 * a crop of the part being talked about is readable.
 */

/** A library entry as Shot's props: its desktop image, its phone crop, its words. */
export function shotProps(s: SiteShot) {
  return {
    src: s.desktop.src, width: s.desktop.width, height: s.desktop.height,
    mobileSrc: s.phone?.src, mobileWidth: s.phone?.width, mobileHeight: s.phone?.height,
    url: s.url, alt: s.alt,
  };
}
export default function Shot({
  src, url, alt, width = 2880, height = 1800,
  mobileSrc, mobileWidth = 780, mobileHeight = 960,
}: {
  src: string;
  url: string;
  alt: string;
  width?: number;
  height?: number;
  mobileSrc?: string;
  mobileWidth?: number;
  mobileHeight?: number;
}) {
  const img = (
    <img src={src} alt={alt} width={width} height={height} loading="lazy" />
  );
  return (
    <figure className="shot-frame">
      <div className="bar" aria-hidden="true">
        <i /><i /><i />
        <span className="url">{url}</span>
      </div>
      {mobileSrc ? (
        <picture>
          <source media="(max-width: 640px)" srcSet={mobileSrc} width={mobileWidth} height={mobileHeight} />
          {img}
        </picture>
      ) : img}
    </figure>
  );
}
